import { useState } from 'react'
import { useStoredWidth } from '../../../hooks/useStoredWidth'
import { ValueNode } from './ValueNode'
import { JsonView } from './JsonView'

type Row = Record<string, unknown>

export function RowInspector({ row, onClose }: { row: Row; onClose: () => void }) {
  const [width, setWidth] = useStoredWidth('inspector-width', 420)
  const [fullscreen, setFullscreen] = useState(false)
  const [tab, setTab] = useState<'fields' | 'json'>('fields')
  const [search, setSearch] = useState('')
  // Strip internal stamps (e.g. __rowIdx added for HITL edit identity) from the inspector view
  const visibleRow = Object.fromEntries(Object.entries(row).filter(([k]) => !k.startsWith('__')))
  const entries = Object.entries(visibleRow)
  const filtered = search ? entries.filter(([k]) => k.toLowerCase().includes(search.toLowerCase())) : entries

  function onDragStart(e: React.MouseEvent) {
    e.preventDefault()
    const startX = e.clientX
    const startW = width
    const onMove = (ev: MouseEvent) => setWidth(Math.min(Math.max(360, startW + startX - ev.clientX), Math.round(window.innerWidth * 0.8)))
    const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp) }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }

  const panelStyle: React.CSSProperties = fullscreen
    ? { position: 'fixed', inset: 0, zIndex: 200, display: 'flex', flexDirection: 'column', background: 'var(--bg-1)', borderLeft: '1px solid var(--line)' }
    : { width, minWidth: 360, position: 'relative', borderLeft: '1px solid var(--line)', background: 'var(--bg-1)', display: 'flex', flexDirection: 'column', flexShrink: 0 }

  return (
    <div style={panelStyle}>
      {!fullscreen && (
        <div onMouseDown={onDragStart} style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 5, cursor: 'col-resize', zIndex: 10 }} />
      )}

      <div className="row" style={{ padding: '10px 14px', borderBottom: '1px solid var(--line-soft)', justifyContent: 'space-between', background: 'var(--bg-2)', flexShrink: 0 }}>
        <span className="mono" style={{ fontSize: 10, color: 'var(--fg-3)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>row inspector</span>
        <div className="row gap-6">
          <button className="btn btn-ghost btn-mono" style={{ fontSize: 11, padding: '2px 8px' }} onClick={() => setFullscreen(f => !f)}>
            {fullscreen ? 'exit' : 'expand'}
          </button>
          <span style={{ cursor: 'pointer', color: 'var(--fg-3)', fontSize: 18, lineHeight: '1', padding: '0 2px' }} onClick={onClose}>×</span>
        </div>
      </div>

      <div className="row" style={{ borderBottom: '1px solid var(--line-soft)', background: 'var(--bg-2)', flexShrink: 0 }}>
        {(['fields', 'json'] as const).map(t => (
          <button key={t} className="btn btn-ghost btn-mono" onClick={() => setTab(t)} style={{
            fontSize: 11, padding: '7px 14px', borderRadius: 0,
            borderBottom: tab === t ? '2px solid var(--accent)' : '2px solid transparent',
            color: tab === t ? 'var(--fg)' : 'var(--fg-3)',
          }}>{t.charAt(0).toUpperCase() + t.slice(1)}</button>
        ))}
      </div>

      {tab === 'fields' && (
        <>
          <div style={{ padding: '7px 14px', borderBottom: '1px solid var(--line-soft)', flexShrink: 0 }}>
            <input className="mono" placeholder="search fields…" value={search} onChange={e => setSearch(e.target.value)}
              style={{ width: '100%', background: 'transparent', border: 'none', outline: 'none', fontSize: 12, color: 'var(--fg)' }} />
          </div>
          <div style={{ overflow: 'auto', flex: 1, minHeight: 0, padding: fullscreen ? '24px 10%' : '14px 16px' }}>
            <div style={{ maxWidth: fullscreen ? 900 : undefined }}>
              {filtered.length === 0
                ? <span style={{ color: 'var(--fg-3)', fontSize: 12 }}>No fields match</span>
                : filtered.map(([k, v]) => <ValueNode key={k} label={k} value={v} depth={0} />)
              }
            </div>
          </div>
        </>
      )}

      {tab === 'json' && <JsonView row={visibleRow} fullscreen={fullscreen} />}
    </div>
  )
}
